import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import { KELAS_LIST, RUANGAN_LIST, FASE_LIST } from "@/lib/constants";

const F_EMPTY = { guru: "", siswa: "", ruangan: "", kelas: "", fase: "" };

export default function Pendampingan() {
  const [siswa, setSiswa] = useState([]);
  const [guru, setGuru] = useState([]);
  const [assignments, setAssignments] = useState({}); // siswa_id -> {guru_id, kelas, ruangan}
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: g }, { data: s }] = await Promise.all([
        api.get("/pendampingan"),
        api.get("/guru", { params: { status: "aktif" } }),
        api.get("/siswa", { params: { status: "aktif" } }),
      ]);
      setGuru(g);
      setSiswa(s);
      const initial = {};
      s.forEach((st) => { initial[st.id] = { guru_id: "", kelas: "", ruangan: "", fase: "" }; });
      p.forEach((it) => {
        initial[it.siswa_id] = {
          guru_id: it.guru_id || "",
          kelas: it.kelas || "",
          ruangan: it.ruangan || "",
          fase: it.fase || "",
        };
      });
      setAssignments(initial);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guruMap = useMemo(() => Object.fromEntries(guru.map((g) => [g.id, g.nama])), [guru]);

  const setField = (siswa_id, key, value) => {
    setAssignments((prev) => ({
      ...prev,
      [siswa_id]: { ...(prev[siswa_id] || { guru_id: "", kelas: "", ruangan: "", fase: "" }), [key]: value },
    }));
  };

  const visibleSiswa = siswa.filter((s) => {
    const a = assignments[s.id] || {};
    if (applied.siswa && !s.nama.toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.guru) {
      const namaGuru = (guruMap[a.guru_id] || "").toLowerCase();
      if (!namaGuru.includes(applied.guru.toLowerCase())) return false;
    }
    if (applied.kelas && a.kelas !== applied.kelas) return false;
    if (applied.ruangan && a.ruangan !== applied.ruangan) return false;
    if (applied.fase && a.fase !== applied.fase) return false;
    return true;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = siswa.map((s) => ({
        siswa_id: s.id,
        guru_id: assignments[s.id]?.guru_id || "",
        kelas: assignments[s.id]?.kelas || "",
        ruangan: assignments[s.id]?.ruangan || "",
        fase: assignments[s.id]?.fase || "",
      }));
      const { data } = await api.post("/pendampingan/bulk", { items });
      toast.success(`Tersimpan: ${data.saved} siswa${data.removed ? `, dihapus: ${data.removed}` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle
        title="Siswa Pendampingan"
        action={
          <button onClick={handleSave} disabled={saving} className="pp-btn-primary" data-testid="pend-save-all">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        }
      />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="pend-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari nama siswa" },
          { key: "guru", type: "text", label: "Guru", placeholder: "Cari nama guru" },
          { key: "kelas", type: "select", label: "Kelas", options: KELAS_LIST },
          { key: "ruangan", type: "select", label: "Ruangan", options: RUANGAN_LIST },
          { key: "fase", type: "select", label: "Fase", options: FASE_LIST },
        ]}
      />

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pend-table">
            <thead>
              <tr className="pp-table-header">
                <th className="text-left px-4 py-3">Nama Siswa</th>
                <th className="text-left px-4 py-3">Ketunaan</th>
                <th className="text-left px-4 py-3 min-w-[130px]">Fase</th>
                <th className="text-left px-4 py-3 min-w-[220px]">Guru Pendamping</th>
                <th className="text-left px-4 py-3 min-w-[140px]">Kelas</th>
                <th className="text-left px-4 py-3 min-w-[160px]">Ruangan</th>
              </tr>
            </thead>
            <tbody>
              {visibleSiswa.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Tidak ada siswa aktif.</td></tr>
              )}
              {visibleSiswa.map((s) => {
                const a = assignments[s.id] || { guru_id: "", kelas: "", ruangan: "", fase: "" };
                return (
                  <tr key={s.id} className="pp-row" data-testid={`pend-row-${s.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{s.nama}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{s.ketunaan || "—"}</td>
                    <td className="px-4 py-2">
                      <select
                        className="pp-input"
                        value={a.fase}
                        onChange={(e) => setField(s.id, "fase", e.target.value)}
                        data-testid={`pend-fase-${s.id}`}
                      >
                        <option value="">— Pilih Fase —</option>
                        {FASE_LIST.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="pp-input"
                        value={a.guru_id}
                        onChange={(e) => setField(s.id, "guru_id", e.target.value)}
                        data-testid={`pend-guru-${s.id}`}
                      >
                        <option value="">— Pilih Guru —</option>
                        {guru.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="pp-input"
                        value={a.kelas}
                        onChange={(e) => setField(s.id, "kelas", e.target.value)}
                        data-testid={`pend-kelas-${s.id}`}
                      >
                        <option value="">— Pilih Kelas —</option>
                        {KELAS_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="pp-input"
                        value={a.ruangan}
                        onChange={(e) => setField(s.id, "ruangan", e.target.value)}
                        data-testid={`pend-ruangan-${s.id}`}
                      >
                        <option value="">— Pilih Ruangan —</option>
                        {RUANGAN_LIST.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end p-4 border-t border-slate-100">
          <button onClick={handleSave} disabled={saving} className="pp-btn-primary" data-testid="pend-save-bottom">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
